import { Stripe } from "@stripe/stripe-js";
import { createPayerSetupIntent } from "../../services/payers";

const AUTHENTICATION_FAILED_MESSAGE =
  "We couldn't verify this card with your bank. Please complete the verification step or try a different card.";

const STRIPE_UNAVAILABLE_MESSAGE =
  "Secure payments are still loading. Please wait a moment and try again.";

type ConfirmCardSetup3DSArgs = {
  stripePromise: Promise<Stripe | null>;
  /** Payer whose Stripe customer the payment method is attached to. */
  payerId: string;
  /** Payment method id returned by `WSStripeInputRef.createPaymentMethod()`. */
  paymentMethodId: string;
};

/**
 * Authenticates a freshly created payment method through a Stripe SetupIntent.
 *
 * 3DS is otherwise requested at `paymentIntent` creation, which happens
 * asynchronously during funds movement — there is no UI left for the cardholder
 * to respond to the challenge at that point, so the payment silently fails.
 * Confirming a SetupIntent here runs the challenge while the cardholder is
 * still on the page and attaches the authenticated method to the payer's
 * Stripe customer, so later off-session charges succeed.
 *
 * Returns a user-facing message when Stripe rejects the card or the challenge
 * is not completed. Failures of the SetupIntent request itself are thrown so
 * they keep the standard API error handling.
 */
export const confirmCardSetup3DS = async ({
  stripePromise,
  payerId,
  paymentMethodId
}: ConfirmCardSetup3DSArgs): Promise<{ error?: string }> => {
  const stripe = await stripePromise;

  if (!stripe) {
    return { error: STRIPE_UNAVAILABLE_MESSAGE };
  }

  const { clientSecret } = await createPayerSetupIntent(payerId);

  // No secret means the service has nothing to confirm, so the card is already
  // good to save — treat it as authenticated rather than as a failure.
  if (!clientSecret) {
    return {};
  }

  const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
    payment_method: paymentMethodId
  });

  if (error) {
    return { error: error.message || AUTHENTICATION_FAILED_MESSAGE };
  }

  if (setupIntent?.status !== "succeeded") {
    return { error: AUTHENTICATION_FAILED_MESSAGE };
  }

  return {};
};
